import { bunja } from 'bunja';
import { atom, type Atom, type ExtractAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';

import { positionBracketBunja } from '@flipster/features/src/shared/position-bracket/state';
import { marginsBunja } from '@flipster/r230522/original/margin';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { atomWithRafThrottle } from '@flipster/react/misc/state/atom-with-raf-throttle';
import Num from '@flipster/utils/num';

import { perpetualSymbolSocketBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { marginTypeBunja, perpAccountBunja, symbolBunja } from './base';
import { formBunja } from './form';
import { computeLiquidationPreview } from './logic/derivation/liquidation';
import { throttledPriceBunja, throttledStateBunja } from './price';

/**
 * Cross/isolated unified order-form liq-price preview. Mirrors prex-core's
 * `calculate_liq_price` for the simulated post-fill state, so the value the
 * user sees on the form lines up with what the engine would liquidate at
 * after their order fills.
 *
 * One-way mode merges into the existing slot-0 position (Increase/Reduce/Flip
 * sim path). Multi-position mode always opens a fresh slot (Open sim path).
 */
export const liquidationPreviewBunja = bunja(() => {
  const { symbol, symbolInfoAtom } = bunja.use(symbolBunja);
  const { askPriceAtom, bidPriceAtom, throttledReferencePriceAtom } = bunja.use(throttledPriceBunja);
  const { askQuantityAtom, bidQuantityAtom } = bunja.use(throttledStateBunja);
  const { orderStateAtom } = bunja.use(formBunja);
  const { marginTypeAtom } = bunja.use(marginTypeBunja);
  const { isOneWayAtom } = bunja.use(perpAccountBunja);
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const { getMarginAtom } = bunja.use(marginsBunja);
  // WS??specs 해석은 positionBracketBunja 한 곳에서만 하고, 검증(bracket.ts)·청산가 프리뷰가
  // 같은 브래킷 atom을 consume하게 한다 (ERD-240 §10, 단일 해석 지점).
  const { positionBracketAtom } = bunja.use(positionBracketBunja);
  const { getTickerAtom, getMarketAtom } = bunja.use(perpetualSymbolSocketBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);

  const markPriceAtom = atom(
    (get) => get(getTickerAtom)?.markPrice ?? get(getMarketAtom)?.markPrice ?? get(throttledReferencePriceAtom),
  );

  /**
   * Existing one-way slot to merge into. Multi-position orders always open a
   * fresh slot regardless of the symbol's open positions, so we return null
   * there — the simulator runs `Open`.
   */
  const targetPositionAtom = atom((get) => {
    if (!get(isOneWayAtom)) return null;
    const positions = get(openPositionsAtom)?.[symbol] ?? [];
    return positions.find((p) => p.slot === 0 && !Num(p.position).isZero()) ?? null;
  });

  // 가격/수량/마진 등 tick 입력을 파라미터로 받아 청산가 프리뷰를 계산하는 팩토리.
  // 같은 로직을 realtime 입력(제출 검증용)과 rAF-throttle 입력(표시용) 두 트랙에 재사용한다.
  const createLiquidationPriceAtom = (src: {
    askPriceAtom: Atom<string>;
    bidPriceAtom: Atom<string>;
    markPriceAtom: Atom<string>;
    askQuantityAtom: Atom<string>;
    bidQuantityAtom: Atom<string>;
    marginAtom: Atom<ExtractAtomValue<typeof getMarginAtom>>;
  }) =>
    atom((get) =>
      computeLiquidationPreview({
        symbolInfo: get(symbolInfoAtom),
        marginType: get(marginTypeAtom),
        targetSymbol: symbol,
        askPrice: get(src.askPriceAtom),
        bidPrice: get(src.bidPriceAtom),
        askQty: get(src.askQuantityAtom),
        bidQty: get(src.bidQuantityAtom),
        markPrice: get(src.markPriceAtom),
        targetPosition: get(targetPositionAtom),
        margins: get(src.marginAtom),
        pswap: get(pswapsAtom)?.[symbol],
        positionBracket: get(positionBracketAtom),
        leverage: get(orderStateAtom).leverage,
      }),
    );

  /** 계산 결과가 같으면 이전 객체 identity를 유지해 소비처 리렌더를 막는다 */
  const isSameLiqPrice = <T extends { long: unknown; short: unknown }>(a: T, b: T) =>
    a.long === b.long && a.short === b.short;

  // source(realtime 입력): 제출 검증 전용. LiquidationInfo는 구독하지 않고 action.tsx가 제출 시점에
  // store.get으로만 읽으므로 지속 구독이 없어 tick당 재계산하지 않는다(제출 시점에만 최신값 계산).
  // 입력 atom이 기존과 동일하므로 제출 검증 동작은 불변.
  const liquidationPriceComputeAtom = createLiquidationPriceAtom({
    askPriceAtom,
    bidPriceAtom,
    markPriceAtom,
    askQuantityAtom,
    bidQuantityAtom,
    marginAtom: getMarginAtom,
  });
  const liquidationPriceAtom = selectAtom(liquidationPriceComputeAtom, (v) => v, isSameLiqPrice);

  // display(rAF-throttle 입력): LiquidationInfo 구독. estimateOrderLiqPrice 재계산·리렌더가 프레임당
  // 1회로 캡핑된다. 표시값이 최대 1프레임(~16ms) 지연되나 제출 검증(위 source)에는 쓰이지 않는다.
  const throttledLiquidationComputeAtom = createLiquidationPriceAtom({
    askPriceAtom: atomWithRafThrottle(askPriceAtom),
    bidPriceAtom: atomWithRafThrottle(bidPriceAtom),
    markPriceAtom: atomWithRafThrottle(markPriceAtom),
    askQuantityAtom: atomWithRafThrottle(askQuantityAtom),
    bidQuantityAtom: atomWithRafThrottle(bidQuantityAtom),
    marginAtom: atomWithRafThrottle(getMarginAtom),
  });
  const throttledLiquidationPriceAtom = selectAtom(throttledLiquidationComputeAtom, (v) => v, isSameLiqPrice);

  return { liquidationPriceAtom, throttledLiquidationPriceAtom };
});
liquidationPreviewBunja.debugLabel = 'liquidationPreviewBunja';
